import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

import { Locale } from '@/enums/Locale';

import { Event } from '@/util/Event';

import steps from '@/data/steps';
import localeCurrency from '@/data/localeCurrency';

import { Store } from '@/enums/Store';

import { InitPayload as CartInitPayload } from '@/store/cart';

@Module({
  namespaced: true
})
export default class App extends VuexModule<ApiStateInterface> {
  locale = Locale.DE;
  promoterId?: number = undefined;
  showInactiveEvents?: string = undefined;
  storeId?: number = undefined;
  currency?: string = undefined;
  step = 0;
  errors: Error[] = [];
  maxErrorCount = 3;

  @Mutation
  SET_LOCALE(locale: Locale) {
    this.locale = locale;
  }

  @Mutation
  SET_PROMOTER_ID(promoterId: number) {
    this.promoterId = promoterId;
  }

  @Mutation
  SET_SHOW_INACTIVE_CATEGORIES(showInactiveEvents: string) {
    this.showInactiveEvents = showInactiveEvents;
  }

  @Mutation
  SET_STORE_ID(storeId: number) {
    this.storeId = storeId;
  }

  @Mutation
  SET_CURRENCY(currency: string) {
    this.currency = currency;
  }

  @Mutation
  ADD_ERROR(error: Error) {
    this.errors.push(error);
    if (this.errors.length > this.maxErrorCount) {
      this.errors.shift();
    }
  }

  @Mutation
  CLEAR_ERRORS() {
    this.errors = [];
  }

  @Mutation
  SET_STEP(step: number) {
    this.step = step;
    window.scrollTo(0, 0);
  }

  @Action
  init({ locale, promoterId, storeId, currency, showInactiveEvents }: InitPayload): void {
    const { commit, dispatch } = this.context;

    commit('SET_LOCALE', locale);
    commit('SET_PROMOTER_ID', promoterId);
    commit('SET_SHOW_INACTIVE_CATEGORIES', showInactiveEvents);
    commit('SET_STORE_ID', storeId);
    commit('SET_CURRENCY', currency);

    dispatch(
      `${Store.CART}/init`,
      { storeId: storeId, currency: localeCurrency[locale] } as CartInitPayload,
      { root: true }
    );
  }

  @Action
  incrementStep() {
    const { getters, commit } = this.context;
    if (!getters.isOnSuccessStep) {
      commit('SET_STEP', this.step + 1);
      commit('CLEAR_ERRORS');
    }
  }

  @Action
  decrementStep() {
    const { getters, commit } = this.context;
    if (getters.isOnStepWrapper) {
      commit('SET_STEP', this.step - 1);
      commit('CLEAR_ERRORS');
      if (this.step == 0) {
        commit(`${Store.LOCATION}/CLEAR_LOCATION`, null, { root: true });
      }
    }
  }

  get isOnStepWrapper(): boolean {
    const { getters } = this.context;

    if (getters.isOnEventSelection) {
      return false;
    }

    if (getters.isOnSuccessStep) {
      return false;
    }

    return !getters.isStepsEmpty;
  }

  get isStepsEmpty(): boolean | never {
    const { getters } = this.context;

    if (getters.steps.length == 0) {
      const isStateInvalid = !getters.isOnEventSelection;
      if (isStateInvalid) {
        throw new Error('State Invalid, steps should be set');
      }
      return true;
    }

    return false;
  }

  get isOnEventSelection(): boolean {
    return this.step == 0;
  }

  get isOnSuccessStep(): boolean {
    const { getters } = this.context;

    if (getters.isStepsEmpty) {
      return false;
    }

    if (getters.isOnEventSelection) {
      return false;
    }

    return this.step == getters.steps.length + 1;
  }

  get steps(): string[] {
    const { rootGetters } = this.context;
    const selectedEvent: Event = rootGetters[`${Store.EVENT}/selectedEventObject`];

    if (!selectedEvent) {
      return [];
    }

    return steps[selectedEvent.type];
  }

  get currentStep(): string | false {
    const { getters } = this.context;

    return getters.steps[this.step - 1];
  }

  get validErrors() {
    return this.errors.filter((error: Error) => {
      return error.message;
    });
  }
}

export interface ApiStateInterface {
  url: string;
  showInactiveEvents?: string;
  locale: Locale;
  promoterId: number;
  step: number;
  errors: Error[];
  maxErrorCount: number;
}

export class InitPayload {
  storeId!: number;
  locale!: Locale;
  promoterId!: number;
  currency: string;
  showInactiveEvents?: string;

  constructor(locale?: Locale, promoterId?: number, storeId?: number, currency?: string, showInactiveEvents?: string) {
    this.locale = locale || Locale.DE;
    this.promoterId = promoterId || 850;
    this.storeId = storeId || 0;
    this.currency = currency || 'EUR';
    this.showInactiveEvents = showInactiveEvents || 'no';
  }
}

export type Init = (payload: InitPayload) => Promise<void>;
